import * as THREE from 'three';
import { clamp, lerp, mulberry32 } from '@/core/rng';
import { VEG_VARIANTS, type VegKind } from '@/world/chunk';
/*
 * The shape vocabulary moved to `render/tree/parts.ts` — see its header for
 * why (an import cycle with the tree generator). Re-exported here so every
 * caller that has always imported them from this module still can.
 */
import {
  BARK, LEAF, bough, cone, faceted, leafCards, limb, lobe, merge, part,
  roughen, solid, type Rgb,
} from '@/render/tree/parts';
export {
  BARK, LEAF, bough, faceted, leafCards, limb, lobe, merge, part, roughen,
  type Rgb,
};
import { growTree } from '@/render/tree/grow';
import { TREE_SPECIES, stageAge } from '@/render/tree/profile';
import { dryness } from '@/render/season';
import { expressed, type PlantPhenotype } from '@/render/phenotype';

/**
 * The silhouettes the world's plants come in — twenty-one of them now.
 *
 * One geometry per kind, one material for all of them, built once and shared by
 * every chunk. Each geometry is modelled **about the plant's base**, so the
 * matrix translation is simply the spot of ground it stands on and an
 * instance's scaleY / scaleXZ scale it from there.
 *
 * What that is NOT is a unit box. This header used to say every build occupies
 * y ∈ [0, 1] and x, z ∈ [−0.5, 0.5], "so an instance's scaleY is its height in
 * metres" — and no build in the kit has ever satisfied it. Measured off
 * `createVegetationKit()`, the far builds top out between **0.2957**
 * (`cushion`) and **1.0700** (`columnar`), and their half-widths run 0.1794
 * (`flower`) to 0.7615 (`spreading`). Only the near variants of one kind are
 * normalised, and only to that kind's own far build (`matchHeight`, which is
 * what law 8 costs in the render tier).
 *
 * So: **drawn height is `scaleY × the kind's build height`**, drawn width is
 * `scaleXZ × twice its half-width`, and `world/chunk.ts`'s `Instance.scaleY`
 * carries the longer version of the same warning. Believing the unit box is
 * how a v0.5 height law came to be argued in a number nothing draws.
 *
 * The shapes exist to be told apart at 200 m, where a plant is a dozen pixels
 * and only its outline survives: a broad flattened crown, a narrow vertical
 * spire, a low trunkless mound, a spray of blades, a flame, a burst of fronds,
 * a bare dead trunk, a curtain reaching down. Everything is vertex coloured
 * white-for-foliage and brown-for-wood with a vertical shade gradient baked
 * in, so each instance's own colour multiplies through while trunks stay
 * woody and undersides stay grounded.
 *
 * Nothing here reads the world. Placement, species and colour are decided in
 * `world/chunk.ts` as a pure function of position; this module only knows how
 * to draw shapes.
 */

export interface VegetationKit {
  /**
   * One geometry per kind, all modelled about the plant's base (see the header
   * for what that does and does not promise about height) — the far tier:
   * cheap enough to be drawn tens of thousands of times. LOD 1+ chunks and the
   * far canopy draw from this record.
   */
  geometry: Record<VegKind, THREE.BufferGeometry>;
  /**
   * The near tier: richer builds of the same silhouettes, in seeded variants
   * (`VEG_VARIANTS` per kind), for the LOD 0 ring where a tree is hundreds of
   * pixels. Same base, same material, same wind, and normalised to the far
   * build's own height so a plant does not resize at the rung-0 boundary.
   */
  near: Record<VegKind, THREE.BufferGeometry[]>;
  material: THREE.Material;
  /**
   * THE DEPTH MATERIAL, and why the kit owns one now.
   *
   * A caster's shadow is drawn with three's own depth material, which knows
   * nothing about this kit's `onBeforeCompile`. That has always been true and
   * has never mattered: the wind sway is not in the depth pass either, and
   * `render/nearDetail.ts` says so in as many words — nothing at grass scale
   * can show it.
   *
   * A burn can. A tree whose crown has been eaten by the vertex morph but
   * whose shadow is still a full canopy is not a subtle artefact; it is the
   * whole illusion, on the ground, in daylight. So the burn morph — and only
   * the burn morph — is mirrored here, and `render/chunkView.ts` hangs this on
   * every vegetation mesh it makes.
   */
  depthMaterial: THREE.Material;
  /** main.ts advances this each frame for the wind sway. */
  wind: { value: number };
  /**
   * The structural half of a plant phenotype, priced for one (kind, variant)
   * mesh: `null` when the phenotype is structurally neutral (the common case,
   * and the zero-cost one). `phenoApply` hangs the values on a mesh via
   * `onBeforeRender`/`onAfterRender`, poking the shared material uniforms for
   * exactly that mesh's draw and restoring neutral after — so every mesh that
   * does NOT participate (far canopy included, which clones this material)
   * renders through bit-exact neutral values. See `createVegetationKit` for
   * the morph itself and `render/phenotype.ts` for the conventions.
   */
  phenoStructural(kind: VegKind, p: PlantPhenotype): PlantPhenoValues | null;
  phenoApply(mesh: THREE.Mesh, values: PlantPhenoValues): void;
  dispose(): void;
}

/** Frozen shader-space values for one mesh: see `phenoStructural`. */
export interface PlantPhenoValues {
  /** (crown lift in build units, foliage-density factor, droop, trunk factor).
   *  Neutral is exactly (0, 1, 0, 1). */
  pheno: readonly [number, number, number, number];
  /** (1/top, lift renormalisation, 0, 0). Neutral is exactly (1, 1, 0, 0). */
  aux: readonly [number, number, number, number];
}



/**
 * Sway coefficient per kind — metres of tip travel per metre of plant height,
 * before the gust curve. A spire is stiff, a crown rolls, grass whips.
 */
const FLEX: Record<VegKind, number> = {
  broadleaf: 0.045,
  conifer: 0.026,
  shrub: 0.05,
  tuft: 0.20,
  // a heavy crown on a short bole barely moves; a reedbed is nothing but
  // movement; a rosette is rigid by construction and a cushion is a rock
  spreading: 0.030,
  reed: 0.26,
  rosette: 0.012,
  cushion: 0.006,
  // a poplar shivers rather than rolls; palm fronds ride every gust; dead
  // wood is done moving; ferns and flower heads live near the grass end
  columnar: 0.032,
  palm: 0.055,
  snag: 0.005,
  fern: 0.16,
  flower: 0.20,
  willow: 0.10,
  // A cactus is a water tank with a skin under pressure and is the stiffest
  // living thing in the kit; a prostrate mat and a wind-hardened krummholz
  // have already lost every argument they were going to have with the wind. A
  // seed plume on a metre of stalk is the opposite — it is nearly all lever —
  // and an umbel is a plume with a plate on top, so it lags rather than whips.
  succulent: 0.008,
  plume: 0.22,
  broom: 0.09,
  creeper: 0.004,
  umbel: 0.19,
  krummholz: 0.010,
  // a four-metre mast does move, but slowly and as one piece
  candle: 0.07,
};

/**
 * How much of the year each kind wears — a sibling of `FLEX`, and read the
 * same way: it is a property of the plant, not of the weather.
 *
 * Grass and herbs cure completely, because that is what curing IS: the
 * standing dead fuel `sim/fire.ts` burns. Woody things barely move, an
 * evergreen almost not at all, and a snag is already dead and has nothing left
 * to lose. Gating on `leaf` alone would cure a conifer's needles exactly as
 * hard as a tuft's blades, which is why this exists.
 */
const CURE: Record<VegKind, number> = {
  tuft: 1.0, reed: 1.0, fern: 0.95, flower: 1.0,
  cushion: 0.60, rosette: 0.60,
  shrub: 0.45,
  broadleaf: 0.18, spreading: 0.18, willow: 0.18, columnar: 0.18, palm: 0.14,
  conifer: 0.08,
  // belt and braces: a snag's geometry is all bark, so `leaf` is already 0
  snag: 0,
  // Bunchgrass and umbellifers cure completely and stand — that is what makes
  // them the fuel `sim/fire.ts` burns, and it is why grassland gets a plume
  // rather than a second shrub. A bolted mast is dead by the end of its own
  // season by definition. A succulent is the one thing on the island that does
  // NOT cure: it is holding water on purpose, which is the whole plant.
  plume: 1.0, umbel: 0.95, candle: 0.85,
  broom: 0.55, creeper: 0.35,
  krummholz: 0.08, succulent: 0.05,
};

/**
 * PLANT PHENOTYPE EXPRESSION — how far each axis moves each kind, at +1 on
 * the normalized scale (`render/phenotype.ts`; ±2 is the clamp, so the drawn
 * extremes are these gains squared). A shared axis means different geometry
 * per kind on purpose — droop is a willow's whole character and nothing a
 * succulent can say — and a zero here is the honest "this kind cannot mean
 * that", never an error.
 *
 * `stature`/`breadth`/`light`/`warmth` are factors (×g at +1, ×1/g at −1)
 * applied per INSTANCE through the matrix and colour in `chunkView.ts`; they
 * therefore survive every LOD rung, cast correct shadows, and are what the
 * pick raycasts. `lift` is build-space units of crown elevation, `density`
 * and `trunk` are factors, `droop` a dimensionless sink strength — all four
 * ride the per-mesh uniform morph below.
 *
 * The numbers are a DESIGN RANGE, not a measurement: they exist so future
 * trait standing has expressive room (an Established ×1.4 stature clears the
 * world's own ×1.2-ish per-plant spread; see `test/phenotype.ts` §dominance),
 * and they will be re-tuned against the eye when a real source ships. Neutral
 * bypasses them entirely, so they cost the shipped world nothing.
 */
export interface PlantExpression {
  stature: number; breadth: number; lift: number; density: number;
  droop: number; trunk: number; light: number; warmth: number;
}
export const PLANT_EXPRESSION: Record<VegKind, PlantExpression> = {
  //                 stature breadth  lift density droop trunk light warmth
  broadleaf:  { stature: 1.42, breadth: 1.40, lift: 0.12, density: 1.30, droop: 0.28, trunk: 1.20, light: 1.30, warmth: 1.12 },
  conifer:    { stature: 1.45, breadth: 1.35, lift: 0.14, density: 1.25, droop: 0.30, trunk: 1.18, light: 1.30, warmth: 1.12 },
  spreading:  { stature: 1.35, breadth: 1.42, lift: 0.10, density: 1.30, droop: 0.26, trunk: 1.22, light: 1.30, warmth: 1.12 },
  columnar:   { stature: 1.45, breadth: 1.30, lift: 0.10, density: 1.28, droop: 0.18, trunk: 1.18, light: 1.30, warmth: 1.12 },
  willow:     { stature: 1.35, breadth: 1.38, lift: 0.08, density: 1.30, droop: 0.50, trunk: 1.20, light: 1.30, warmth: 1.12 },
  palm:       { stature: 1.40, breadth: 1.30, lift: 0.10, density: 1.22, droop: 0.35, trunk: 1.15, light: 1.28, warmth: 1.12 },
  shrub:      { stature: 1.35, breadth: 1.38, lift: 0.05, density: 1.30, droop: 0.22, trunk: 1.00, light: 1.30, warmth: 1.12 },
  krummholz:  { stature: 1.30, breadth: 1.35, lift: 0.03, density: 1.25, droop: 0.20, trunk: 1.10, light: 1.28, warmth: 1.10 },
  broom:      { stature: 1.32, breadth: 1.32, lift: 0.00, density: 1.20, droop: 0.25, trunk: 1.00, light: 1.28, warmth: 1.10 },
  // the ground layer: silhouette and pigment carry it; a blade has no crown
  // to lift and its parts sit on the ground, where the density morph
  // deliberately does not reach (see `part`)
  tuft:       { stature: 1.35, breadth: 1.32, lift: 0.00, density: 1.15, droop: 0.30, trunk: 1.00, light: 1.32, warmth: 1.14 },
  reed:       { stature: 1.35, breadth: 1.25, lift: 0.00, density: 1.15, droop: 0.30, trunk: 1.00, light: 1.30, warmth: 1.12 },
  fern:       { stature: 1.32, breadth: 1.35, lift: 0.00, density: 1.15, droop: 0.35, trunk: 1.00, light: 1.30, warmth: 1.12 },
  flower:     { stature: 1.35, breadth: 1.30, lift: 0.00, density: 1.35, droop: 0.25, trunk: 1.00, light: 1.32, warmth: 1.16 },
  umbel:      { stature: 1.35, breadth: 1.30, lift: 0.00, density: 1.35, droop: 0.22, trunk: 1.00, light: 1.30, warmth: 1.14 },
  plume:      { stature: 1.35, breadth: 1.30, lift: 0.00, density: 1.30, droop: 0.28, trunk: 1.00, light: 1.30, warmth: 1.12 },
  candle:     { stature: 1.38, breadth: 1.25, lift: 0.00, density: 1.32, droop: 0.15, trunk: 1.08, light: 1.30, warmth: 1.12 },
  creeper:    { stature: 1.20, breadth: 1.35, lift: 0.00, density: 1.15, droop: 0.00, trunk: 1.00, light: 1.28, warmth: 1.10 },
  cushion:    { stature: 1.25, breadth: 1.32, lift: 0.00, density: 1.18, droop: 0.00, trunk: 1.00, light: 1.28, warmth: 1.10 },
  rosette:    { stature: 1.30, breadth: 1.30, lift: 0.00, density: 1.15, droop: 0.15, trunk: 1.00, light: 1.28, warmth: 1.10 },
  // a water tank and a dead trunk have very little left to express
  succulent:  { stature: 1.32, breadth: 1.25, lift: 0.00, density: 1.10, droop: 0.00, trunk: 1.12, light: 1.25, warmth: 1.10 },
  snag:       { stature: 1.30, breadth: 1.25, lift: 0.00, density: 1.00, droop: 0.10, trunk: 1.20, light: 1.20, warmth: 1.08 },
};


/**
 * Stand a near build exactly as tall as its far build.
 *
 * Every build in this file is modelled about its own base and scaled by
 * `Instance.scaleY` in `chunkView.ts`, so the two tiers only agree if every
 * variant of a kind tops out at the same y. Without this, giving a variant a
 * shorter stem or a flatter fan — which is what actually makes variants
 * differ — would also make the plant change *size* as it crossed the rung-0
 * boundary, and law 8 says a plant at a coarse LOD is the same plant at every
 * finer one. Coarse LODs may omit; they may not resize. So vary the shape
 * freely and normalise the height back, and the variance lands entirely in
 * silhouette where it belongs.
 *
 * **The `target` is the KIND's own far build, not 1.** This file does not
 * normalise across kinds and never has: measured off the built kit, `creeper`
 * tops out at 0.4218, `cushion` at 0.2957, `krummholz` at 0.6574 and
 * `columnar` at 1.0700. So `Instance.scaleY` is a scale factor, NOT metres of
 * drawn plant — drawn height is `scaleY × this kind's build height`, and any
 * argument of the form "this kind is taller than the biome around it" has to
 * be made in that second number. `tools/form.ts`'s §"as drawn" table is the
 * only instrument that reports it, and the reason it exists is that a v0.5
 * height law was justified against the first number and failed against the
 * second. Normalising every kind to 1 here would resize nine of the
 * twenty-one on the spot, so the fix is to measure honestly, not to flatten.
 */
/**
 * SCALE THE POSITION-LIKE ATTRIBUTES TOO.
 *
 * `pcen` (a vertex's offset from its own part's centroid) and `lobe` (its
 * mass's centre) are both in BUILD SPACE — they are positions, not weights —
 * and `BufferGeometry.scale()` touches `position` and `normal` and nothing
 * else. So every height normalisation below has been quietly leaving them at
 * the pre-normalisation scale.
 *
 * It was invisible while `pcen` only drove the phenotype density morph, which
 * is off in the shipped world and a few percent wrong when it is on. The burn
 * morph made it visible at once: collapsing a foliage mass by subtracting a
 * `pcen` that is 5% too small leaves 5% of the mass behind, so a fully burnt
 * tree kept a black nub at every cluster instead of losing its crown. Nine
 * hundred and fifty-six foliage triangles, none of them collapsing, is how it
 * was found — a probe over the built geometry, not an eye at the screen.
 */
function scaleBuildSpace(g: THREE.BufferGeometry, kx: number, ky: number, kz: number): void {
  for (const name of ['pcen', 'lobe'] as const) {
    const a = g.attributes[name];
    if (!a) continue;
    const arr = a.array as Float32Array;
    for (let i = 0; i < a.count; i++) {
      arr[i * 3] *= kx; arr[i * 3 + 1] *= ky; arr[i * 3 + 2] *= kz;
    }
    a.needsUpdate = true;
  }
}

function matchHeight(g: THREE.BufferGeometry, target: number): THREE.BufferGeometry {
  g.computeBoundingBox();
  const top = g.boundingBox!.max.y;
  if (top > 1e-6 && Math.abs(top - target) > 1e-6) {
    const k = target / top;
    g.scale(1, k, 1);
    scaleBuildSpace(g, 1, k, 1);
  }
  return g;
}

/**
 * The same promise kept UNIFORMLY — for the three hero trees only (2026-08-19).
 *
 * `matchHeight` scales Y alone, and for a build whose proportions are the
 * whole point that is a distortion: measured with a scale-hook over the kit
 * build, the near broadleaf variants were being STRETCHED ×1.05–1.35 to the
 * far blob's 0.98 (the crowns read as saplings — tall, thin lobes) and the
 * near oaks SQUASHED ×0.70–0.82 to the far umbrella's 0.673 (the billows read
 * as plates). The conifer was within 1.5% and did not care. A hero build is
 * designed in its own proportion, so it is fitted in all three axes at once —
 * the top lands on the far build's top exactly as before, and the width goes
 * with it rather than being left behind. The other eighteen kinds keep
 * `matchHeight`: their near builds were tuned under it, and re-fitting them
 * uniformly would resize things this pass never looked at.
 */
function fitHeight(g: THREE.BufferGeometry, target: number): THREE.BufferGeometry {
  g.computeBoundingBox();
  const top = g.boundingBox!.max.y;
  if (top > 1e-6 && Math.abs(top - target) > 1e-6) {
    const k = target / top;
    g.scale(k, k, k);
    scaleBuildSpace(g, k, k, k);
  }
  return g;
}

/**
 * A spray of thin blades. Each blade is one triangle and its mirror, so a tuft
 * still shows from behind without a two-sided material — twelve triangles for
 * the whole plant, which is what lets grassland afford thousands of them.
 * Deterministic: the layout comes from a fixed seed, never from Math.random.
 */
function blades(
  count: number, seed: number,
  opts: { lean?: number; width?: number; drop?: number } = {},
): THREE.BufferGeometry {
  const rnd = mulberry32(seed);
  const pos = new Float32Array(count * 18);
  const nor = new Float32Array(count * 18);
  // front winding, then the same three corners reversed for the back face
  const WIND = [0, 1, 2, 1, 0, 2];
  // multipliers on the stock tuft: a fern is these blades leaning far out and
  // arching down, a flower's stalks are these blades thin and upright
  const leanK = opts.lean ?? 1, widthK = opts.width ?? 1, drop = opts.drop ?? 0;
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + rnd() * 0.8;
    const root = 0.04 + rnd() * 0.10;          // how far off centre it sprouts
    const h = (0.55 + rnd() * 0.45) * (1 - drop * 0.35);   // blade length
    const lean = (0.12 + rnd() * 0.16) * leanK;   // how far the tip flops out
    const w = (0.026 + rnd() * 0.022) * widthK;   // half-width at the root
    const ca = Math.cos(a), sa = Math.sin(a);
    const tip = root + lean * h;
    // the blade widens across its own outward direction, so it always presents
    // some width to a camera looking at the tuft from outside
    const v: [number, number, number][] = [
      [ca * root + sa * w, 0, sa * root - ca * w],
      [ca * root - sa * w, 0, sa * root + ca * w],
      // `drop` arches the tip down as it reaches out — a frond, not a blade
      [ca * tip, h * (1 - drop), sa * tip],
    ];
    const ex = v[1][0] - v[0][0], ey = v[1][1] - v[0][1], ez = v[1][2] - v[0][2];
    const fx = v[2][0] - v[0][0], fy = v[2][1] - v[0][1], fz = v[2][2] - v[0][2];
    let nx = ey * fz - ez * fy, ny = ez * fx - ex * fz, nz = ex * fy - ey * fx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    for (let f = 0; f < 6; f++) {
      const o = i * 18 + f * 3;
      const side = f < 3 ? 1 : -1;
      const p = v[WIND[f]];
      pos[o] = p[0]; pos[o + 1] = p[1]; pos[o + 2] = p[2];
      nor[o] = nx * side; nor[o + 1] = ny * side; nor[o + 2] = nz * side;
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  return g;
}

/**
 * A sheaf of straight stiff wands — the deliberate opposite of `blades`.
 *
 * `blades` tapers every blade to a point because that is what grass does, and
 * everything built out of it therefore reads soft. A broom's stems are
 * constant-width sticks that go up and stop, and a seed plume is a stick that
 * WIDENS at the top; both of those are impossible to say with a triangle that
 * ends in a point, and both are why chaparral and grassland stop reading as
 * the same scrub. Each wand is a quad and its mirror — four triangles — for
 * the same reason `blades` doubles its faces: no two-sided material.
 *
 * `head` is the tip half-width as a multiple of the root's: 1 is a parallel
 * wand, 3 is a plume. Deterministic from a fixed seed, never Math.random.
 */
function wands(
  count: number, seed: number,
  opts: { lean?: number; width?: number; head?: number; low?: number } = {},
): THREE.BufferGeometry {
  const rnd = mulberry32(seed);
  const pos = new Float32Array(count * 36);
  const nor = new Float32Array(count * 36);
  // two triangles a face, wound front then back
  const WIND = [0, 1, 2, 0, 2, 3, 2, 1, 0, 3, 2, 0];
  const leanK = opts.lean ?? 0.14, wK = opts.width ?? 0.030;
  const headK = opts.head ?? 1, low = opts.low ?? 0;
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + rnd() * 0.9;
    const root = 0.02 + rnd() * 0.07;
    const h = low + (1 - low) * (0.62 + rnd() * 0.38);
    const lean = leanK * (0.5 + rnd());
    const w = wK * (0.7 + rnd() * 0.6);
    const hw = w * headK;
    const ca = Math.cos(a), sa = Math.sin(a);
    const tip = root + lean;
    const v: [number, number, number][] = [
      [ca * root + sa * w, low, sa * root - ca * w],
      [ca * root - sa * w, low, sa * root + ca * w],
      [ca * tip - sa * hw, h, sa * tip + ca * hw],
      [ca * tip + sa * hw, h, sa * tip - ca * hw],
    ];
    const ex = v[1][0] - v[0][0], ey = v[1][1] - v[0][1], ez = v[1][2] - v[0][2];
    const fx = v[2][0] - v[0][0], fy = v[2][1] - v[0][1], fz = v[2][2] - v[0][2];
    let nx = ey * fz - ez * fy, ny = ez * fx - ex * fz, nz = ex * fy - ey * fx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    for (let t = 0; t < 12; t++) {
      const o = i * 36 + t * 3;
      const side = t < 6 ? 1 : -1;
      const p = v[WIND[t]];
      pos[o] = p[0]; pos[o + 1] = p[1]; pos[o + 2] = p[2];
      nor[o] = nx * side; nor[o + 1] = ny * side; nor[o + 2] = nz * side;
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  return g;
}

/**
 * A broad, slightly flattened crown on a short trunk — the workhorse of oak
 * woodland and riparian forest. The crown fills most of the plant's height on
 * purpose: a shallow crown plus a wide instance scale reads as a plate on a
 * stick, which is the failure mode this shape exists to avoid.
 *
 * Three offset lobes of falling size make the outline irregular rather than a
 * lollipop, and it stays at three on purpose. This build is only ever drawn at
 * rung 1 and beyond — 1,024 m out and further — and `tools/foliage.ts` prices
 * an 8.1 m tree at 10.1 px there and a crown LOBE at 2.4 px, under the
 * three-pixel threshold this codebase treats as the floor of a signal. Lobes
 * were briefly added here and it was the wrong tier: 0.84 M triangles for a
 * feature nothing can resolve. The lobe work belongs in `broadleafNear`, which
 * covers 0–768 m where the same lobe is 7.3 px. 51 triangles.
 */
function broadleaf(): THREE.BufferGeometry {
  const f = FLEX.broadleaf;
  return merge([
    part(cone(0.085, 0.44, 5, 0), BARK, f, 0.7),
    part(solid(new THREE.IcosahedronGeometry(0.47, 0))
      .scale(1.0, 0.88, 0.98).translate(0, 0.56, 0), LEAF, f, 0.72),
    part(solid(new THREE.OctahedronGeometry(0.32, 0))
      .scale(1.15, 1.0, 1.1).translate(0.10, 0.66, -0.08), LEAF, f, 0.82),
    part(solid(new THREE.OctahedronGeometry(0.27, 0))
      .scale(1.15, 1.05, 1.15).translate(-0.16, 0.50, 0.10), LEAF, f, 0.66),
  ]);
}

/**
 * A tall narrow spire on a bare lower trunk. Read against a hillside of broad
 * crowns this is unmistakable at any distance, which is the whole point of it.
 *
 * Three stacked tapering cones, and it stays at three for the same reason
 * `broadleaf` stays at three lobes: this build begins at 1,024 m, where the
 * whole spire is 17 px and a single tier step is under two. `coniferNear`
 * already swings 4–6 tiers per seed, and that is the tier where a tier can be
 * seen. 60 triangles.
 */
function conifer(): THREE.BufferGeometry {
  const f = FLEX.conifer;
  return merge([
    part(cone(0.065, 0.30, 5, 0), BARK, f, 0.6),
    part(cone(0.50, 0.46, 5, 0.14), LEAF, f, 0.62),
    part(cone(0.36, 0.40, 5, 0.42), LEAF, f, 0.74),
    part(cone(0.22, 0.34, 5, 0.66), LEAF, f, 0.84),
  ]);
}

/**
 * A low wide multi-lobe mound with no visible trunk — chaparral, coastal
 * scrub, the understory of an open woodland. Octahedra, because eight facets
 * per lobe is all a mound needs. 24 triangles.
 */
function shrub(): THREE.BufferGeometry {
  const f = FLEX.shrub;
  // A shrub is 1.8 m: 6.7 px at 340 m and 2.3 px at 1,024 m where this build
  // starts. The whole plant is barely a signal out there, so its lobes cannot
  // be one — `shrubNear` carries the detail.
  return merge([
    part(solid(new THREE.OctahedronGeometry(0.42, 0))
      .scale(1.15, 0.95, 1.1).translate(0, 0.40, 0), LEAF, f, 0.55),
    part(solid(new THREE.OctahedronGeometry(0.30, 0))
      .scale(1.1, 1.0, 1.2).translate(0.14, 0.52, -0.10), LEAF, f, 0.62),
    part(solid(new THREE.OctahedronGeometry(0.26, 0))
      .scale(1.2, 1.2, 1.1).translate(-0.16, 0.66, 0.12), LEAF, f, 0.7),
  ]);
}

/** Grassland and understory: six blades, twelve triangles, no trunk. */
function tuft(): THREE.BufferGeometry {
  return merge([part(blades(6, 0x7a5f), LEAF, FLEX.tuft, 0.55)]);
}

/**
 * An oak: a wide flat crown on a short bole, spread across four lobes that
 * reach outward rather than upward.
 *
 * It exists to be told apart from `broadleaf` at a distance, because oak
 * woodland and riparian forest were 0.03 apart on `tools/form.ts`' silhouette
 * distance — both were 70% broadleaf, and in life the difference between them
 * is precisely this: a river tree grows up toward the light in a crowded gallery,
 * an oak stands alone in the open and grows out. So the bole is half the height
 * of a broadleaf's and the lobes sit low and far out, which reads as a disc from
 * above and as a mushroom from the side. 68 triangles.
 */
function spreading(): THREE.BufferGeometry {
  const f = FLEX.spreading;
  const lobes: THREE.BufferGeometry[] = [part(cone(0.10, 0.30, 5, 0), BARK, f, 0.7)];
  // A DOME on a short bole, 1.1× as wide as it is tall — not the 2.15× umbrella
  // it was until 2026-08-19. The width of a spreading tree as drawn is this
  // build's width × `chunk.ts`'s ratio for the kind (1.20–2.25, the column
  // whose whole point is that an oak woodland is wider than a river wood),
  // and the two were being MULTIPLIED: measured on the shipped island, the
  // big oak by the close station stood 12.7 m tall and 56 m across, every
  // oak 4.1–5.1× as wide as tall. No tree is, and the near build could only
  // ever be a table under that envelope. The ratio column keeps the job; this
  // build stops doing it a second time. Same five lobes, same 115 triangles,
  // same top (0.673), so nothing changes height.
  // WIDE AGAIN (2026-08-19, Griffin, having seen the dome: "wider, like they
  // were — the canopy was really cool"): the lobes stand where the umbrella's
  // did, so the drawn crown spans what it spanned — but they are ROUND, kept
  // round under the ratio by `lobe`, so the canopy is billows, not plates.
  // …and then, seen, put back exactly as it was (Griffin: "rounded and wide,
  // not balls"): the five lobes at the umbrella's positions, squashed and
  // stretched, NOT kept round under the ratio — the canopy stretches with
  // its crown into one broad rounded cap. The near build matches.
  const at: Array<[number, number, number, number]> = [
    [0.40, 0.44, 0.00, 0.34], [-0.36, 0.40, 0.10, 0.30],
    [0.08, 0.50, -0.40, 0.31], [-0.06, 0.38, 0.38, 0.28],
  ];
  for (const [dx, dy, dz, r] of at) {
    lobes.push(part(solid(new THREE.IcosahedronGeometry(r, 0))
      .scale(1.25, 0.62, 1.25).translate(dx, dy, dz), LEAF, f, 0.74));
  }
  lobes.push(part(solid(new THREE.IcosahedronGeometry(0.30, 0))
    .scale(1.15, 0.60, 1.15).translate(0, 0.52, 0), LEAF, f, 0.86));
  return merge(lobes);
}

/**
 * A reedbed: a tight sheaf of tall vertical blades, taller than it is wide and
 * always in company. `blades()` already draws exactly this — the difference
 * from a tuft is the count, the narrowness and the flex, which is why marsh
 * and grassland stop reading alike. 26 triangles.
 */
function reed(): THREE.BufferGeometry {
  return merge([part(blades(13, 0x3c81), LEAF, FLEX.reed, 0.70)]);
}

/**
 * A rosette — agave, yucca, the dry-country plant that answers drought by
 * being rigid and radial rather than soft and lobed. Eight stiff leaves
 * fanning out and up from one point, so from above it is a star where every
 * other dry-country plant is a blob. 32 triangles.
 */
function rosette(detail = 0, seed = 0x9d01): THREE.BufferGeometry {
  const f = FLEX.rosette;
  const rnd = mulberry32(seed);
  // Structure first, phase second. Every variant of this plant used to draw
  // the same thirteen leaves at the same tilt and differ only in which way
  // they happened to be turned — `tools/foliage.ts` measured 0.0328 mean pair
  // distance, which is three rosettes that silhouette alike. These three draws
  // happen before the loop, so a variant differs in SHAPE: how many leaves it
  // has, how far it opens, and how far it reaches.
  const N = (detail ? 11 : 8) + Math.floor(rnd() * 5);
  const tilt = 0.42 + rnd() * 0.44;        // an upright spike, or a flat fan
  const reach = 0.80 + rnd() * 0.30;
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 + 0.17 + rnd() * 0.22;
    // a leaf is one long thin box, tipped outward from vertical; the near
    // tier interleaves a shorter inner whorl so the fan reads as layered
    const inner = detail ? i % 2 : 0;
    const len = (inner ? 0.66 : 0.92) * reach * (0.88 + rnd() * 0.24);
    const leaf = new THREE.BoxGeometry(0.10, len, 0.03).toNonIndexed()
      .translate(0, len / 2, 0)
      .rotateX((inner ? tilt * 0.61 : tilt) + (rnd() - 0.5) * 0.12)
      .rotateY(a);
    parts.push(part(solid(leaf), LEAF, f, i % 2 ? 0.62 : 0.84));
  }
  return merge(parts);
}

/**
 * An alpine cushion: a low tight dome hugging the ground, which is the shape
 * every plant converges on where the wind never stops. Deliberately the
 * flattest thing in the kit — at 0.22 m tall and nearly twice as wide, it
 * reads as texture on the ground rather than as a plant standing on it, which
 * is exactly right and is what separates alpine from chaparral. 20 triangles.
 */
function cushion(detail = 0, seed = 0xcd01): THREE.BufferGeometry {
  const f = FLEX.cushion;
  const rnd = mulberry32(seed);
  // Structure first. What variance this had was mostly in its HEIGHT, and
  // `matchHeight` now normalises that away — correctly, because a variant that
  // stood shorter than its own far build was a law-8 resize, not variety. So
  // the difference has to be in plan: how round the dome is, and how many
  // shoulders it grows. A cushion is a colony, and colonies are lumpy.
  const oval = 0.78 + rnd() * 0.44;            // round dome, or an oval mat
  const lobes = 1 + Math.floor(rnd() * 3);
  const parts = [
    part(solid(new THREE.IcosahedronGeometry(0.50, detail))
      .scale(oval, 0.32, 2.0 - oval).translate(0, 0.13, 0), LEAF, f, 0.58),
  ];
  for (let i = 0; i < lobes; i++) {
    const a = rnd() * Math.PI * 2, r = 0.12 + rnd() * 0.16;
    const s = 0.62 + rnd() * 0.62;
    parts.push(part(solid(new THREE.OctahedronGeometry(0.30, detail))
      .scale(1.2 * s, 0.40, 1.2 * s)
      .translate(Math.cos(a) * r, 0.14 + rnd() * 0.05, Math.sin(a) * r), LEAF, f, 0.70));
  }
  return merge(parts);
}

/**
 * A poplar: one narrow vertical flame of foliage on a short clear stem. The
 * shape is nearly all height — `chunk.ts` gives it a ratio around 0.14–0.24 —
 * so beside a broad river crown it reads as an exclamation mark, which is the
 * whole of its job on a water margin. 50 triangles.
 */
function columnar(detail = 0, seed = 0xa5e0): THREE.BufferGeometry {
  const f = FLEX.columnar;
  const rnd = mulberry32(seed);
  const sway = detail ? (rnd() - 0.5) * 0.06 : 0;   // a slight S in the flame
  const parts = [
    part(cone(0.10, 0.16, detail ? 7 : 5, 0), BARK, f, 0.6),
    part(solid(new THREE.IcosahedronGeometry(0.30, detail))
      .scale(1.0, 1.55, 1.0).translate(sway, 0.50, 0), LEAF, f, 0.62),
    part(solid(new THREE.OctahedronGeometry(0.20, detail))
      .scale(1.0, 1.35, 1.0).translate(-sway, 0.80, 0), LEAF, f, 0.82),
  ];
  if (detail) {
    parts.push(leafCards([
      [sway, 0.50, 0, 0.30, 1.55],
      [-sway, 0.80, 0, 0.20, 1.35],
    ], 18, 0.042, seed ^ 0xc0a1, f, 0.62));
  }
  return merge(parts);
}

/**
 * A palm: a bare leaning stem under one burst of drooping fronds. Every part
 * of the silhouette is unlike everything else on the island — trunk with no
 * canopy along it, crown wider than it is deep, fronds instead of mass — so
 * one palm names a coast from any distance that resolves it. 94 triangles.
 */
function palm(detail = 0, seed = 0x5a01): THREE.BufferGeometry {
  const f = FLEX.palm;
  const rnd = mulberry32(seed);
  // Structure first — this was the weakest kind in the whole kit at 0.0162
  // mean pair distance, four palms that differed only in frond phase. A stem
  // height, a lean, a frond count and a frond length drawn per variant turn
  // that into a squat broad-crowned palm and a tall sparse drooping one.
  const stemH = 0.60 + rnd() * 0.30;
  const lean = 0.03 + rnd() * 0.16;
  const N = (detail ? 7 : 6) + Math.floor(rnd() * 4);
  const frondL = 0.40 + rnd() * 0.24;
  const droopBase = 0.30 + rnd() * 0.32;
  const crownY = stemH + 0.04;
  const parts: THREE.BufferGeometry[] = [
    // the stem leans a little; rotating about the base keeps the foot planted
    part(solid(new THREE.CylinderGeometry(0.030, 0.055, stemH, detail ? 7 : 5))
      .translate(0, stemH / 2, 0).rotateZ(lean), BARK, f, 0.55),
  ];
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 + rnd() * 0.5;
    const droop = droopBase + rnd() * 0.28;
    // a frond: one long flattened box, drooping outward from the crown point.
    // The crown point rides the stem's lean. The near tier bends each frond
    // in the middle — the kink is most of what makes a frond read as one.
    const frond = new THREE.BoxGeometry(frondL, 0.016, 0.11).toNonIndexed()
      .translate(frondL / 2, 0, 0)
      .rotateZ(-droop)
      .rotateY(a)
      .translate(-0.075, crownY, 0);
    parts.push(part(solid(frond), LEAF, f, i % 2 ? 0.68 : 0.86));
    if (detail) {
      const tipX = frondL * Math.cos(-droop), tipY = crownY + frondL * Math.sin(-droop);
      const outer = new THREE.BoxGeometry(frondL * 0.58, 0.012, 0.08).toNonIndexed()
        .translate(frondL * 0.29, 0, 0)
        .rotateZ(-droop - 0.55)
        .rotateY(a)
        .translate(Math.cos(a) * tipX - 0.075, tipY, -Math.sin(a) * tipX);
      parts.push(part(solid(outer), LEAF, f, i % 2 ? 0.62 : 0.80));
    }
  }
  return merge(parts);
}

/**
 * A snag — the tree that died standing. A tapering trunk snapped short of a
 * point, three bare branch stubs, no crown at all; every part wears wood's
 * colour, and `chunk.ts` tints the instance bone-grey outright. A forest with
 * its own standing dead reads as *old*, which no arrangement of living crowns
 * can say. 40 triangles.
 */
function snag(detail = 0, seed = 0x6b01): THREE.BufferGeometry {
  const f = FLEX.snag;
  const rnd = mulberry32(seed);
  const seg = detail ? 6 : 4;
  const limb = (r0: number, r1: number, len: number, tilt: number, yaw: number, y: number) =>
    part(solid(new THREE.CylinderGeometry(r0, r1, len, seg, 1, true))
      .translate(0, len / 2, 0).rotateZ(tilt).rotateY(yaw).translate(0, y, 0),
      BARK, f, 0.7);
  const parts = [
    part(solid(new THREE.CylinderGeometry(0.020, 0.070, 0.98, detail ? 6 : 5, 1, true))
      .translate(0, 0.49, 0), BARK, f, 0.5),
  ];
  const limbs = detail ? 5 : 3;
  for (let i = 0; i < limbs; i++) {
    const y = 0.42 + (i / limbs) * 0.44 + rnd() * 0.06;
    const side = i % 2 ? -1 : 1;
    parts.push(limb(
      0.005 + rnd() * 0.004, 0.016 + rnd() * 0.012, 0.16 + rnd() * 0.20,
      side * (0.85 + rnd() * 0.45), rnd() * Math.PI * 2, y,
    ));
  }
  return merge(parts);
}

/**
 * A fern: the tuft's own blades, reaching far out and arching down. Wider
 * than it is tall and darker than the grass beside it, it is the damp forest
 * floor made visible — redwood and river-gallery understory. 16 triangles.
 */
function fern(): THREE.BufferGeometry {
  return merge([part(blades(8, 0x5e22, { lean: 2.4, width: 2.0, drop: 0.34 }),
                     LEAF, FLEX.fern, 0.42)]);
}

/**
 * Wildflowers: a few thin stalks under small bright heads. The stalks keep
 * their own green whatever the instance colour, so the tint `chunk.ts` deals —
 * gold, dusty pink, white — lands on the heads alone and reads as bloom
 * rather than as a strange-coloured plant. 44 triangles.
 */
function flower(detail = 0, seed = 0x91c3): THREE.BufferGeometry {
  const f = FLEX.flower;
  const heads = detail ? 6 : 4;
  const parts: THREE.BufferGeometry[] = [
    part(blades(heads, seed, { lean: 0.45, width: 0.6 }), [0.30, 0.42, 0.24], f, 0.7),
  ];
  const rnd = mulberry32(seed + 1);
  for (let i = 0; i < heads; i++) {
    const a = (i / heads) * Math.PI * 2 + rnd() * 1.1;
    const r = 0.08 + rnd() * 0.14;
    const h = 0.72 + rnd() * 0.26;
    parts.push(part(solid(new THREE.OctahedronGeometry(0.05 + rnd() * 0.015, 0))
      .scale(1, 0.8, 1).translate(Math.cos(a) * r, h, Math.sin(a) * r), LEAF, f, 0.9));
  }
  return merge(parts);
}

/**
 * A willow: a full head draped by a bell of foliage falling nearly to the
 * ground. The drape is SOLID — the first cut hung loose triangle strips off
 * the crown rim, and stretched by per-instance scale they read as detached
 * shards, not as a tree. A closed bell tucking inward at the hem is the
 * weeping silhouette at any distance; the near tier breaks its surface with
 * a few wide flaps seated *into* the bell, so the drape reads combed rather
 * than smooth. ~70 / ~150 triangles.
 */
function willow(detail = 0, seed = 0x77b2): THREE.BufferGeometry {
  const f = FLEX.willow;
  const rnd = mulberry32(seed);
  // Widened in v0.5: this kind measured WEAKEST of all fourteen at 0.0372,
  // five builds that were one tree. A willow's variety in life is entirely in
  // how far the curtain falls and how tightly it tucks, so those are the two
  // spans that grew — a barely-weeping young head and a full curtain to the
  // ground. `drapeH` is capped at 0.68 on purpose: the drape hangs from 0.70,
  // so anything past that puts the hem underground and the tuck stops reading.
  const headR = 0.24 + rnd() * 0.16;
  const drapeH = 0.40 + rnd() * 0.28;                 // how far the hem falls
  const hem = 0.14 + rnd() * 0.22;
  const parts: THREE.BufferGeometry[] = [
    // a little trunk shows at the hem's tuck
    part(cone(0.06, 0.24, 5, 0), BARK, f, 0.6),
    // the head: full, and WIDER than the drape's rim — the bell must vanish
    // up into the head, or its top edge reads as a ledge around the crown
    part(solid(new THREE.IcosahedronGeometry(headR, detail))
      .scale(1.28, 0.92 + rnd() * 0.1, 1.28).translate(0, 0.70 + rnd() * 0.05, 0),
      LEAF, f, 0.8),
    // the drape: widest under the head, tucking inward toward the hem, and
    // reaching almost to the ground — no other crown's mass hangs DOWN
    part(solid(new THREE.CylinderGeometry(headR * 1.12, hem, drapeH, detail ? 9 : 7, 1))
      .translate(0, 0.70 - drapeH / 2, 0), LEAF, f, 0.46),
  ];
  if (detail) {
    /**
     * Combing and trailing clusters — and every radius below now RIDES
     * `headR` and `drapeH` instead of being a constant.
     *
     * This was the whole reason widening `headR` and `drapeH` did nothing.
     * `tools/foliage.ts` reported this kind at 0.0372 before those spans grew
     * and **0.0360 after**, which was the tell: the probe's descriptor is
     * dominated by max radius over height, and max radius was pinned by the
     * flap ring at a flat 0.33–0.39 and by the card lobes at a flat 0.41 —
     * both larger than the head they were supposed to be decorating. A
     * parameter that a later constant overrides is not a parameter, and the
     * measurement is what said so; nothing about the code looked wrong.
     */
    const ring = headR * 1.05 + hem * 0.25;
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + rnd() * 0.6;
      const r = ring * (0.92 + rnd() * 0.18);
      const flapH = drapeH * (0.6 + rnd() * 0.25);
      // …and the hem is FLOORED, which the first cut of this was not: with
      // `drapeH` now reaching 0.68 the longest flaps hung 0.116 below the
      // plant's own base, and a geometry that starts under y = 0 buries that
      // share of itself at every scale the instance is drawn at.
      // `test/visual-kits.ts` caught it; nothing about the expression looked
      // wrong, and the shipped kit's deepest build is 0.030.
      const mid = Math.max(flapH / 2 + 0.04, 0.70 - drapeH * (0.55 + rnd() * 0.20));
      parts.push(part(solid(new THREE.BoxGeometry(0.17, flapH, 0.025))
        .rotateZ(0.10 + rnd() * 0.08)
        .rotateY(a)
        .translate(Math.cos(a) * r, mid, Math.sin(a) * r),
        LEAF, f, 0.55));
    }
    // The drape's card lobe describes the bell, so its centre is the bell's
    // middle and its vertical reach is the bell's own half-height — and the
    // squash is SOLVED against the ground rather than picked, because
    // `leafCards` scatters over the whole sphere and a lobe that reaches
    // further down than its centre sits high puts cards underground. That is
    // what -0.116 was; the flaps beside it were innocent.
    const drapeMid = 0.70 - drapeH * 0.5;
    const drapeR = (headR * 1.12 + hem) * 0.5;
    parts.push(leafCards([
      [0, 0.70, 0, headR * 1.32, 0.95],
      [0, drapeMid, 0, drapeR, Math.min(1.30, (drapeMid - 0.06) / Math.max(drapeR, 1e-3))],
    ], 28, 0.052, seed ^ 0x11b7, f, 0.55));
  }
  return merge(parts);
}

// ===========================================================================
// v0.5's seven. Every one of them is a shape the kit could not previously
// say, and five of the seven are TALLER than the biome that grows them —
// see `VegKind` in `world/chunk.ts` for why that was the selection rule and
// not "which pair does the probe name".
//
// Each takes `(detail, seed)` like the rest of the second-generation builders,
// so the far tier and the near variants come out of one function and cannot
// drift apart. Structure varies with the seed, never just phase: §13's own
// finding was that four builds differing only in which way they were turned
// measured 0.0162 apart and read as one plant.
// ===========================================================================

/**
 * Dry country's column: a fluted trunk with a few raised arms.
 *
 * The one tall thing in a biome whose mean plant is 1.4 m, which is the whole
 * reason it exists — a dry scrub full of correctly-modelled knee-high shapes
 * photographs as bare gravel, and §13 recorded exactly that happening to
 * marsh. Nothing else on the island is a vertical cylinder, so it names its
 * country from any range that resolves it at all. Arms are 0..2 by seed: a
 * young column has none, and a stand of them therefore has an age structure.
 */
function succulent(detail = 0, seed = 0x5c01): THREE.BufferGeometry {
  const f = FLEX.succulent;
  const rnd = mulberry32(seed);
  const seg = detail ? 9 : 5;              // the flutes
  const bodyH = 0.80 + rnd() * 0.19;
  const R = 0.13 + rnd() * 0.05;
  const parts: THREE.BufferGeometry[] = [
    part(solid(new THREE.CylinderGeometry(R * 0.86, R, bodyH, seg, 1))
      .translate(0, bodyH / 2, 0), LEAF, f, 0.58),
  ];
  const arms = Math.floor(rnd() * 3);
  for (let i = 0; i < arms; i++) {
    const a = rnd() * Math.PI * 2;
    const y0 = 0.26 + rnd() * 0.24;
    const out = 0.13 + rnd() * 0.10;
    const up = 0.20 + rnd() * 0.26;
    const ar = R * (0.52 + rnd() * 0.18);
    // the elbow, built along +x and then turned: a stub out from the trunk,
    // then a riser straight up beside it. An arm that only reached OUT would
    // read as a branch; the vertical return is what makes it a saguaro.
    parts.push(part(solid(new THREE.CylinderGeometry(ar, ar, out, seg, 1, true))
      .rotateZ(Math.PI / 2).translate(R * 0.7 + out / 2, y0, 0).rotateY(a),
      LEAF, f, 0.62));
    parts.push(part(solid(new THREE.CylinderGeometry(ar * 0.92, ar, up, seg, 1, !detail))
      .translate(R * 0.7 + out, y0 + up / 2, 0).rotateY(a), LEAF, f, 0.66));
  }
  return merge(parts);
}

/**
 * Bunchgrass: a low tussock under a spray of pale seed plumes.
 *
 * Twice a tuft's height and a third of its width, and the plumes carry the
 * signal — they sit in the top fifth of the plant where `part`'s own shade
 * gradient makes them the brightest thing on it, so a grassland reads as
 * stippled with standing straw rather than as mown baize. It is also the
 * kind that makes the fire model visible: `CURE` is 1.0, so the plumes go
 * hay-coloured in the dry season while the shrubs beside them barely move.
 */
function plume(detail = 0, seed = 0x9101): THREE.BufferGeometry {
  const f = FLEX.plume;
  const rnd = mulberry32(seed);
  // How the plant splits between tussock and plume, per seed — and it has to
  // be a WIDE split, because that split is the only thing about this kind the
  // eye can read at range. `tools/foliage.ts` measured 0.0230 ("barely
  // separated") at 0.42–0.66; 0.26–0.78 is a low mat of blades under a tall
  // sparse spray at one end and a dense tussock barely topped at the other,
  // which is genuinely the range one bunchgrass species holds.
  const tussock = 0.26 + rnd() * 0.52;
  const heads = (detail ? 7 : 4) + Math.floor(rnd() * 3);
  return merge([
    part(blades(detail ? 8 : 5, seed ^ 0x77, { lean: 0.9 + rnd() * 0.7, width: 1.2 })
      .scale(0.75 + tussock * 0.5, tussock / 0.8, 0.75 + tussock * 0.5), LEAF, f, 0.45),
    // head 3.2: the wand widens toward its tip instead of tapering, which is
    // the one thing `blades` cannot draw and the whole difference from a tuft
    part(wands(heads, seed ^ 0x19, { lean: 0.10 + rnd() * 0.12, width: 0.013, head: 3.2 }),
      LEAF, f, 0.75),
  ]);
}

/**
 * Chaparral's broom: a sheaf of stiff near-leafless wands.
 *
 * A shrub's height and a reed's width, and that pairing is the whole of why
 * chaparral stops reading as coastal scrub — the two were 0.18 apart with
 * both fielding a low mound in slightly different amounts. A besom is not a
 * mound at any distance. The near tier adds the burl it grows out of, which
 * is what tells it from a sheaf of grass once you are close enough to ask.
 */
function broom(detail = 0, seed = 0xb701): THREE.BufferGeometry {
  const f = FLEX.broom;
  const rnd = mulberry32(seed);
  const N = (detail ? 13 : 8) + Math.floor(rnd() * 5);
  const open = 0.08 + rnd() * 0.18;        // a tight besom, or a splayed one
  const parts = [part(wands(N, seed ^ 0x31, { lean: open, width: 0.026 }),
                      LEAF, f, 0.48)];
  if (detail) {
    parts.push(part(solid(new THREE.IcosahedronGeometry(0.10, 0))
      .scale(1.3, 0.7, 1.3).translate(0, 0.05, 0), BARK, f, 0.5));
  }
  return merge(parts);
}

/**
 * The strand mat: a prostrate radial spray of trailing runners.
 *
 * The flattest thing in the kit — `chunk.ts` gives it a ratio of 3.1–4.8
 * against a cushion's 1.9–2.9 — and deliberately so. Shore was under 0.25
 * from four other biomes and §13's finding is that a short biome cannot be
 * separated by silhouette at all, so this one is not trying to be: at a metre
 * across and a hand high it is GROUND, wearing a grey-green nobody else on the
 * island wears, over pale sand. Its separation is colour and texture, and the
 * honest way to read it is that it moves the probe and is *meant* to be judged
 * at the gate instead.
 */
function creeper(detail = 0, seed = 0xc701): THREE.BufferGeometry {
  const f = FLEX.creeper;
  const rnd = mulberry32(seed);
  const N = (detail ? 9 : 6) + Math.floor(rnd() * 4);
  const reach = 0.40 + rnd() * 0.12;
  const lift = 0.40 + rnd() * 0.30;        // how far the runners tip up
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 + rnd() * 0.7;
    const len = reach * (0.70 + rnd() * 0.55);
    parts.push(part(solid(
      new THREE.BoxGeometry(len, detail ? 0.07 : 0.10, 0.12).toNonIndexed()
        .translate(len / 2, 0, 0)
        .rotateZ(lift * (0.7 + rnd() * 0.6))
        .rotateY(a)
        .translate(0, 0.06, 0)),
      LEAF, f, i % 2 ? 0.58 : 0.82));
  }
  return merge(parts);
}

/**
 * The umbellifer: a bare stalk under one flat pale head.
 *
 * Head-high on ground that is otherwise mounds, and the head is built as a
 * ring of little plates rather than as a dome because FLAT is the signal —
 * a dome is a flower and a plate is an umbel. The stalk keeps its own green
 * whatever the instance colour is, which is `flower`'s trick one storey up:
 * the near-white `chunk.ts` deals lands on the head alone and reads as bloom
 * rather than as a strange-coloured plant.
 */
function umbel(detail = 0, seed = 0x0b01): THREE.BufferGeometry {
  const f = FLEX.umbel;
  const rnd = mulberry32(seed);
  const stalkH = 0.62 + rnd() * 0.22;
  const rays = (detail ? 9 : 5) + Math.floor(rnd() * 4);
  const headR = 0.22 + rnd() * 0.20;       // a tight head, or a wide plate
  const parts: THREE.BufferGeometry[] = [
    part(solid(new THREE.CylinderGeometry(0.012, 0.024, stalkH, detail ? 5 : 3, 1, true))
      .translate(0, stalkH / 2, 0), [0.32, 0.44, 0.26], f, 0.62),
  ];
  // How FLAT the head is, per seed — a tight globe of florets or a dinner
  // plate. `tools/foliage.ts` measured this kind at 0.0185 mean pair distance
  // ("barely separated") when only `headR` and the ray count varied: three
  // heads of different widths at the same squash are one head, because the
  // descriptor the probe uses — and the eye — reads how mass is distributed up
  // the plant, and three discs at one height distribute it identically.
  const plate = 0.30 + rnd() * 0.55;
  const dome = (1 - plate) * 0.22;         // how far the tight head bulges up
  for (let i = 0; i < rays; i++) {
    const a = (i / rays) * Math.PI * 2 + rnd() * 0.4;
    const r = headR * (0.5 + rnd() * 0.55);
    parts.push(part(solid(new THREE.OctahedronGeometry(0.050 + rnd() * 0.03, 0))
      .scale(0.9 + plate * 0.7, 0.24 + (1 - plate) * 0.5, 0.9 + plate * 0.7)
      .translate(Math.cos(a) * r,
                 stalkH + 0.03 + rnd() * 0.05 + dome * (1 - r / Math.max(headR, 1e-3)),
                 Math.sin(a) * r),
      LEAF, f, 0.88));
  }
  /**
   * Some plants branch, and about half of them do here.
   *
   * This is the honest fix for a measurement that would not move: `plate`
   * above widened the head's shape and `tools/foliage.ts` still reported
   * 0.0180, unchanged to four figures. The reason is a property of the plant
   * rather than of the constants — the probe's descriptor is a mass profile
   * up the height, `matchHeight` puts every variant's head at the top, and
   * ~90% of an umbel's vertices ARE the head, so three variants distribute
   * their mass identically no matter how differently the head is shaped.
   * **A statistic cannot see a difference in a band it has no mass in.**
   *
   * A second smaller head partway down a side branch is what a real
   * umbellifer does, it is the one change that puts vertices in the middle
   * bands, and it is visible for the same reason it is measurable.
   */
  const branch = rnd();
  if (branch > 0.45) {
    const a = rnd() * Math.PI * 2;
    const bY = stalkH * (0.42 + rnd() * 0.22);
    const bOut = 0.12 + rnd() * 0.12;
    const bR = headR * (0.45 + rnd() * 0.25);
    parts.push(part(solid(new THREE.CylinderGeometry(0.008, 0.014, bOut * 1.6, 3, 1, true))
      .rotateZ(-0.9).translate(bOut * 0.5, bY + bOut * 0.5, 0).rotateY(a),
      [0.32, 0.44, 0.26], f, 0.62));
    for (let i = 0; i < (detail ? 5 : 3); i++) {
      const ra = (i / (detail ? 5 : 3)) * Math.PI * 2;
      parts.push(part(solid(new THREE.OctahedronGeometry(0.038 + rnd() * 0.02, 0))
        .scale(1.2, 0.4, 1.2)
        .translate(Math.cos(a) * bOut + Math.cos(ra) * bR * 0.7,
                   bY + bOut, Math.sin(a) * bOut + Math.sin(ra) * bR * 0.7),
        LEAF, f, 0.84));
    }
  }
  if (detail) {
    // a couple of cut leaves low on the stalk — the plant is not a lollipop
    parts.push(part(blades(4, seed ^ 0x5a, { lean: 2.2, width: 1.4, drop: 0.3 })
      .scale(0.55, 0.30, 0.55), [0.30, 0.42, 0.25], f, 0.5));
  }
  return merge(parts);
}

/**
 * Krummholz — a conifer the wind would not let stand.
 *
 * Every lobe streams the same way from one leaning stem, and that shared
 * FLAG is the whole read: a bush is radial and this is not, so even at a low
 * count of pixels a fellfield of these looks combed. It is the only crown in
 * the kit whose mass sits below a metre, which is what separates alpine from
 * chaparral — §13 left that pair at 0.24 and named alpine's `shrub` share as
 * the cause, and this is what the share was moved into.
 */
function krummholz(detail = 0, seed = 0x4b01): THREE.BufferGeometry {
  const f = FLEX.krummholz;
  const rnd = mulberry32(seed);
  const flag = rnd() * Math.PI * 2;
  const lobes = (detail ? 5 : 3) + Math.floor(rnd() * 3);
  const bend = 0.42 + rnd() * 0.34;
  const parts: THREE.BufferGeometry[] = [
    part(solid(new THREE.CylinderGeometry(0.022, 0.045, 0.42, detail ? 6 : 4, 1, true))
      .translate(0, 0.21, 0).rotateZ(bend).rotateY(flag + Math.PI), BARK, f, 0.5),
  ];
  for (let i = 0; i < lobes; i++) {
    const t = i / Math.max(1, lobes - 1);
    const r = 0.08 + t * (0.26 + rnd() * 0.12);
    const lr = 0.24 - t * 0.07 + rnd() * 0.06;
    // Octahedron far (8 triangles) and icosahedron near (20), rather than the
    // icosahedron-at-`detail` every other lobed kind uses. `tools/foliage.ts`
    // priced the obvious version at 88 far and a near MEAN of 492 — five
    // subdivided icosahedra on a plant that is a metre tall and lives at
    // 2,000 m where almost nothing grows. A krummholz mat is a low dark blob
    // with a comb in it; the comb is the lobe LAYOUT and costs nothing.
    parts.push(part(solid(detail
        ? new THREE.IcosahedronGeometry(lr, 0)
        : new THREE.OctahedronGeometry(lr, 0))
      .scale(1.5, 0.60, 1.15)
      .translate(Math.cos(flag) * r + (rnd() - 0.5) * 0.08,
                 0.18 + (1 - t) * 0.30 + rnd() * 0.06,
                 Math.sin(flag) * r + (rnd() - 0.5) * 0.08),
      LEAF, f, 0.48 + t * 0.26));
  }
  return merge(parts);
}

/**
 * The mast: a rosette that has bolted, and then it dies.
 *
 * Four metres over ground whose median plant is 1.4 m, and the narrowest
 * ratio in the table — `chunk.ts` gives it 0.10–0.21 against a poplar's
 * 0.11–0.28 — so it is a line on the sky, findable from the air, and rare
 * enough (0.01–0.03 in three biomes) that finding one is an event rather than
 * a fixture. The rosette it grew out of is still at its foot: that skirt is
 * the tell that this is a rosette's last act and not a young tree, and it is
 * the only thing standing between "a rare plant" and "a thin snag".
 */
function candle(detail = 0, seed = 0xca01): THREE.BufferGeometry {
  const f = FLEX.candle;
  const rnd = mulberry32(seed);
  const stemH = 0.42 + rnd() * 0.14;
  const whorls = (detail ? 7 : 5) + Math.floor(rnd() * 3);
  const parts: THREE.BufferGeometry[] = [
    part(blades(detail ? 8 : 5, seed ^ 0x2c, { lean: 1.9, width: 1.9 })
      .scale(1.0, 0.24, 1.0), LEAF, f, 0.5),
    part(solid(new THREE.CylinderGeometry(0.014, 0.030, stemH, detail ? 6 : 4, 1, true))
      .translate(0, stemH / 2, 0), BARK, f, 0.55),
  ];
  for (let i = 0; i < whorls; i++) {
    const t = i / Math.max(1, whorls - 1);
    // the bloom tapers to the tip, so the mast reads as a spike and not a rod
    const r = (0.11 - t * 0.065) * (0.8 + rnd() * 0.5);
    parts.push(part(solid(new THREE.OctahedronGeometry(r, 0))
      .scale(1.5, 0.75, 1.5).translate(0, stemH + t * (0.98 - stemH), 0),
      LEAF, f, 0.70 + t * 0.22));
  }
  return merge(parts);
}


// ===========================================================================
// The near tier — richer builds of the same silhouettes, for LOD 0 chunks.
//
// The far shapes above are budgeted for being drawn tens of thousands of
// times across kilometres; inside the close ring a tree is hundreds of pixels
// and twenty polygons of crown reads as a paper model. These carry three to
// five times the triangles and — the half that matters more — come in
// *seeded variants*, so a close stand is not one tree stamped forty times.
// Which variant a plant wears is `chunk.ts`'s decision (a hash of the plant's
// own position, like everything else about it); the far tier collapses every
// variant back to the one cheap shape, which only omission ever notices.
// ===========================================================================








/** Near shrub: one subdivided heart under seeded satellite lobes, with a
 *  modest scatter of cards — shrubs are numerous, so they stay cheap. */
function shrubNear(seed: number): THREE.BufferGeometry {
  const f = FLEX.shrub;
  const rnd = mulberry32(seed);
  const heartSq = 0.9 + rnd() * 0.12;
  const parts: THREE.BufferGeometry[] = [
    part(solid(new THREE.IcosahedronGeometry(0.40, 1))
      .scale(1.15, heartSq, 1.1).translate(0, 0.40, 0), LEAF, f, 0.55),
  ];
  const lobes: Array<readonly [number, number, number, number, number]> =
    [[0, 0.40, 0, 0.40, heartSq]];
  // 4..6, up from 2..4. Shrubs are the second most numerous thing in the near
  // ring after broadleaf, so this is the change that costs most — but a scrub
  // hillside IS the frame in half the island's biomes, and three octahedra was
  // reading as a beanbag rather than a bush.
  const satsN = 4 + Math.floor(rnd() * 3);
  for (let i = 0; i < satsN; i++) {
    const a = rnd() * Math.PI * 2, r = 0.14 + rnd() * 0.10;
    const lr = 0.20 + rnd() * 0.09;
    const sq = 0.95 + rnd() * 0.25;
    const lx = Math.cos(a) * r, ly = 0.42 + rnd() * 0.24, lz = Math.sin(a) * r;
    parts.push(part(solid(new THREE.OctahedronGeometry(lr, 1))
      .scale(1.1, sq, 1.15).translate(lx, ly, lz), LEAF, f, 0.58 + rnd() * 0.16));
    lobes.push([lx, ly, lz, lr, sq]);
  }
  parts.push(leafCards(lobes, 16, 0.05, seed ^ 0x5b2b, f, 0.58));
  return merge(parts);
}


/**
 * THE NEAR TREES ARE GROWN, NOT BUILT (2026-08-21).
 *
 * The five tree families' near builders are `render/tree/grow.ts` now — one
 * grammar over a species profile, a seed and an age, with a real branch
 * hierarchy under the foliage. `TREES.md` has the craft the hand-built ones
 * taught and every rule of it survived the move; what changed is that a
 * species is a table entry rather than a function, so the variant pool is
 * bounded by the frame's budget instead of by how many builders anybody wrote.
 *
 * **Age is pinned at maturity here, and that is law 8 speaking.** `fitHeight`
 * normalises every near variant to the kind's ONE far build, so a sapling
 * variant would be stretched to an adult's height at LOD 0 — a small adult,
 * which is the exact thing the generator exists to prevent. Life stages need
 * either per-stage far builds (which `TREES.md` §3 forbids: "far builds stay
 * cheap") or a per-instance decision that picks a far build too. Until that is
 * settled, the world grows mature trees and the workshop scrubs the ages.
 *
 * The old builders are kept and still compile — `?grove=` in the workshop and
 * the before/after stations are how the two are compared, and deleting the
 * thing you are measuring against is how a regression becomes invisible.
 */
function grownNear(species: string, seed: number): THREE.BufferGeometry {
  return growTree({
    species: TREE_SPECIES[species],
    seed,
    // the middle of `mature` — see the note above about why it is fixed
    age01: stageAge('mature'),
  }).geometry;
}

export function createVegetationKit(): VegetationKit {
  const geometry: Record<VegKind, THREE.BufferGeometry> = {
    broadleaf: broadleaf(),
    conifer: conifer(),
    shrub: shrub(),
    tuft: tuft(),
    spreading: spreading(),
    reed: reed(),
    rosette: rosette(),
    cushion: cushion(),
    columnar: columnar(),
    palm: palm(),
    snag: snag(),
    fern: fern(),
    flower: flower(),
    willow: willow(),
    succulent: succulent(),
    plume: plume(),
    broom: broom(),
    creeper: creeper(),
    umbel: umbel(),
    krummholz: krummholz(),
    candle: candle(),
  };

  // The near tier, one builder per kind; a kind with one variant simply gets
  // its single richer build. Seeds step by a prime so no two variants of any
  // kind share a layout stream.
  const nearBuild: Record<VegKind, (v: number) => THREE.BufferGeometry> = {
    broadleaf: (v) => grownNear('broadleaf', 0x61a0 + v * 97),
    conifer: (v) => grownNear('conifer', 0x72b0 + v * 97),
    shrub: (v) => shrubNear(0x83c0 + v * 97),
    // tuft and reed are the two kinds built straight out of `blades`, and both
    // measured "barely separated" (0.0270 and 0.0241) because a wider blade
    // count barely moves a spray's silhouette. What does move it is how far
    // the blades flop out and how the mass sits up the height, so each variant
    // now takes its own lean, width and proportion — and `matchHeight` puts
    // the height back, so the difference is all in shape.
    tuft: (v) => {
      const t = v / Math.max(1, VEG_VARIANTS.tuft - 1);
      return merge([part(
        blades(8 + v * 3, 0x7a60 + v * 131, { lean: 0.65 + t * 1.55, width: 0.80 + t * 0.85 })
          .scale(0.78 + t * 0.62, 1.30 - t * 0.55, 0.78 + t * 0.62),
        LEAF, FLEX.tuft, 0.55)]);
    },
    spreading: (v) => grownNear('oak', 0x94d0 + v * 97),
    reed: (v) => {
      const t = v / Math.max(1, VEG_VARIANTS.reed - 1);
      return merge([part(
        blades(16 + v * 6, 0x3c82 + v * 131, { lean: 0.45 + t * 1.20, width: 0.85 + t * 0.55 })
          .scale(0.70 + t * 0.66, 1.26 - t * 0.46, 0.70 + t * 0.66),
        LEAF, FLEX.reed, 0.70)]);
    },
    rosette: (v) => rosette(1, 0x9d01 + v * 97),
    cushion: (v) => cushion(1, 0xcd01 + v * 97),
    columnar: (v) => grownNear('poplar', 0xa5e0 + v * 97),
    palm: (v) => palm(1, 0x5a01 + v * 97),
    snag: (v) => snag(1, 0x6b01 + v * 97),
    fern: (v) => {
      const t = v / Math.max(1, VEG_VARIANTS.fern - 1);
      // a tight upright shuttlecock at one end, a broad flat arching crown at
      // the other — the two things a fern actually is, depending on its light
      return merge([part(
        blades(10 + v * 3, 0x5e23 + v * 131,
               { lean: 1.55 + t * 1.70, width: 1.55 + t * 0.95, drop: 0.16 + t * 0.34 })
          .scale(0.80 + t * 0.60, 1.24 - t * 0.46, 0.80 + t * 0.60),
        LEAF, FLEX.fern, 0.42)]);
    },
    flower: (v) => flower(1, 0x91c3 + v * 97),
    willow: (v) => grownNear('willow', 0x77b2 + v * 97),
    succulent: (v) => succulent(1, 0x5c01 + v * 97),
    // The two built out of `wands` step their seeds by a different prime than
    // the rest. `tuft` and `reed` measured "barely separated" because a wand
    // COUNT barely moves a sheaf's outline; what moves it is the splay and the
    // proportion, and both of those are drawn per seed inside the builders
    // (`open` in broom, `lean`/`tussock` in plume) rather than derived from
    // the variant index. `matchHeight` then puts the height back, so the whole
    // of the difference lands in shape.
    plume: (v) => plume(1, 0x9101 + v * 131),
    broom: (v) => broom(1, 0xb701 + v * 131),
    creeper: (v) => creeper(1, 0xc701 + v * 97),
    umbel: (v) => umbel(1, 0x0b01 + v * 97),
    krummholz: (v) => krummholz(1, 0x4b01 + v * 97),
    candle: (v) => candle(1, 0xca01 + v * 97),
  };
  const near = {} as Record<VegKind, THREE.BufferGeometry[]>;
  /** Each kind's build height — the morph's normaliser (`uPhenoAux.x`). */
  const tops = {} as Record<VegKind, number>;
  for (const k of Object.keys(geometry) as VegKind[]) {
    // every variant stands as tall as the kind's far build — see `matchHeight`
    geometry[k].computeBoundingBox();
    const top = geometry[k].boundingBox!.max.y;
    tops[k] = top;
    const fit = (k === 'broadleaf' || k === 'spreading' || k === 'conifer' || k === 'willow' || k === 'columnar')
      ? fitHeight : matchHeight;
    near[k] = Array.from({ length: Math.max(1, VEG_VARIANTS[k]) },
      (_, v) => fit(nearBuild[k](v), top));
  }

  // The cure weight, derived from the attribute that is already there rather
  // than written during building: `leaf` marks foliage, and multiplying it by
  // the kind's own share gives one number per vertex that is 0 on every trunk
  // and every snag for free. Deliberately NOT added to `ATTRS` — `merge()`
  // reads every ATTRS name off every part, and the blade and leaf-card paths
  // reach it without going through `part()`, so a new required attribute
  // there throws.
  for (const k of Object.keys(geometry) as VegKind[]) {
    for (const g of [geometry[k], ...near[k]]) {
      const lf = g.attributes.leaf.array as Float32Array;
      const c = new Float32Array(lf.length);
      for (let i = 0; i < lf.length; i++) c[i] = lf[i] * CURE[k];
      g.setAttribute('cure', new THREE.BufferAttribute(c, 1));
    }
  }

  const material = new THREE.MeshStandardMaterial({
    roughness: 0.95, metalness: 0, vertexColors: true,
  });
  /**
   * The depth material, carrying the burn morph and nothing else. Deliberately
   * NOT the whole vertex program: the wind and the phenotype morph are
   * sub-texel at every range a shadow of this kit is resolved at, and a second
   * copy of them is a second thing to keep in step for no visible return. The
   * burn is not sub-texel — it removes the crown.
   */
  const depthMaterial = new THREE.MeshDepthMaterial({
    depthPacking: THREE.RGBADepthPacking,
  });
  depthMaterial.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>',
        '#include <common>\nattribute float leaf;\nattribute vec3 pcen;\nattribute vec2 aBurn;')
      .replace('#include <begin_vertex>',
        '#include <begin_vertex>\n  transformed -= pcen * (leaf * aBurn.x);');
  };
  const wind = { value: 0 };
  /**
   * The structural-phenotype uniforms, shared by every program compiled off
   * this material (the far canopy's clone included — deliberately, exactly as
   * `uDry` is shared). NEUTRAL VALUES ARE A BIT-EXACT NO-OP: the morph below
   * is written so (0,1,0,1)/(1,1,0,0) multiplies by one and adds zero, which
   * is exact in IEEE arithmetic — so every mesh that never touches them (all
   * of them, today) draws the same pixels it drew before this existed.
   *
   * Per-MESH values are poked in `phenoApply` via `onBeforeRender` and
   * restored to neutral in `onAfterRender`, so a participating mesh pays two
   * vec4 uploads and every other mesh on the program reads neutral. This is
   * the per-mesh-uniform pattern rather than a per-instance attribute for a
   * measured reason: chunk vegetation shares its geometries across every
   * chunk, and r160 releases GL buffers per *geometry* — a per-chunk wrapper
   * geometry either leaks its instanced buffer on eviction or, disposed,
   * deletes the kit's shared buffers under every other live chunk. Structure
   * therefore lands at the (chunk, kind, variant) grain — the population
   * grain a trait source will actually vary at — while stature, breadth and
   * pigment ride the instance matrix and colour at full per-plant grain
   * (`chunkView.ts`).
   */
  const phenoU = { value: new THREE.Vector4(0, 1, 0, 1) };
  const phenoAuxU = { value: new THREE.Vector4(1, 1, 0, 0) };
  const PHENO_NEUTRAL: readonly [number, number, number, number] = [0, 1, 0, 1];
  const PHENO_AUX_NEUTRAL: readonly [number, number, number, number] = [1, 1, 0, 0];
  // wind: every instance sways in place, phased by where it stands in the
  // world, leaning along one prevailing direction. `flex` carries the per-kind
  // stiffness and the root-to-tip profile, and dividing by the instance's own
  // xz scale makes the sway a distance in metres rather than a share of the
  // plant's width — so a spire and a crown of the same height move together.
  //
  // And per-pixel FOLIAGE: a crown drawn as clean solid lobes reads as a blob
  // however good its silhouette is. Two octaves of world-anchored noise break
  // the surface — a luminance clump (leaves grow in masses, and masses shade
  // each other) and a normal dither (a canopy's surface is a thousand facets,
  // not one) — both faded out before they can alias, exactly as the ground's
  // grain is. World-anchored means the texture is the same texture at every
  // rung and in every worker; the crown sways through its own field, which
  // reads as leaves stirring. The far canopy clones this material, so a far
  // crown and a near crown wear the same leaves until distance fades them.
  material.onBeforeCompile = (sh) => {
    sh.uniforms.uTime = wind;
    // assigned HERE and only here: farCanopy clones this material and calls
    // this same onBeforeCompile, so both hold the one shared object
    sh.uniforms.uDry = dryness;
    // the phenotype morph's two vec4s — shared objects too, neutral unless a
    // mesh's own onBeforeRender says otherwise for exactly its draw
    sh.uniforms.uPheno = phenoU;
    sh.uniforms.uPhenoAux = phenoAuxU;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>',
        '#include <common>\nuniform float uTime;\nattribute float flex;\n'
        + 'attribute float leaf;\nvarying vec3 vVegW;\nvarying float vLeaf;'
        + '\nattribute float cure;\nvarying float vCure;\nvarying vec4 vCrownShadow;'
        + '\nattribute vec3 lobe;\nattribute vec3 pcen;'
        + '\nattribute vec2 aBurn;\nvarying vec2 vBurn;'
        + '\nuniform vec4 uPheno;\nuniform vec4 uPhenoAux;')
      // a round mass stays round under the instance's ratio: its vertices are
      // pulled back toward their own centre by sy/sxz in x and z (see `part`),
      // and the normal is pre-skewed so mat3(instanceMatrix) lands it where a
      // uniformly scaled sphere's normal would be
      .replace('#include <beginnormal_vertex>', `#include <beginnormal_vertex>
        #ifdef USE_INSTANCING
        if (dot(lobe, lobe) > 0.0) {
          float sxzN = max(length(instanceMatrix[0].xyz), 1e-3);
          float syN = length(instanceMatrix[1].xyz);
          objectNormal.xz *= syN / sxzN;
        }
        #endif`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        {
          // ---- the structural phenotype morph (uPheno/uPhenoAux; the
          // neutral values are a bit-exact identity — multiplies by one and
          // adds zero — so a mesh that never pokes them draws what it always
          // drew; plantMorphVertex is the CPU mirror and the tests hold the
          // two together) ----
          // foliage density: a mass scales about its own centre; pcen is zero
          // on wood and on anything rooted at the ground, so neither can move
          transformed += pcen * (uPheno.y - 1.0);
          /*
           * ---- THE BURN (2026-08-20) ----
           *
           * A fire took this plant's leaves, not its wood. pcen is already
           * each vertex's offset from its own PART's centroid — the same
           * channel the density morph above rides — so scaling a foliage part
           * about its own centre by (1 − char) consumes it exactly where it
           * stood, and at char = 1 it is a point: no triangles, no silhouette,
           * nothing left but the skeleton it hung on. Wood carries pcen 0 and
           * cannot move, so the tree that remains is the tree that was there.
           *
           * That is the whole of it. No second build, no burnt variant of any
           * kind, and it works on all twenty-one — the grass, the ferns and
           * the litter clumps included — because leaf and pcen were
           * already the right two facts about every vertex in the kit.
           *
           * aBurn is a per-INSTANCE attribute, which is what a burn is: a
           * scar's edge runs THROUGH a stand, and the plants either side of it
           * share a mesh. An unbound attribute reads (0,0,0,1) in GL, so every
           * mesh that has never heard of fire draws char 0 — bit-exactly what
           * it drew before this existed.
           */
          transformed -= pcen * (leaf * aBurn.x);
          // trunk mass: wood thickens about the plant axis, fading with
          // height so a limb tip stays seated in the mass it carries
          float phTrunk = mix(uPheno.w, 1.0, smoothstep(0.15, 0.7, transformed.y * uPhenoAux.x));
          transformed.xz *= mix(phTrunk, 1.0, leaf);
          // crown lift: what stands above the base rises by uPheno.x build
          // units, renormalised so the build's top does not move (stature is
          // a different axis, and it rides the instance matrix)
          transformed.y = (transformed.y + uPheno.x * smoothstep(0.06, 0.55, transformed.y * uPhenoAux.x)) * uPhenoAux.y;
          // droop: parts sink with their squared distance from the axis,
          // foliage most, and never below their own ground
          float phSink = uPheno.z * (0.35 + 0.65 * leaf) * dot(transformed.xz, transformed.xz) * uPhenoAux.x;
          transformed.y -= min(phSink, max(transformed.y - 0.01, 0.0));
        }
        {
          vec4 iw = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          float ph = iw.x * 0.043 + iw.z * 0.031;
          // loose parts lag one another (2026-08-19, the willow pass): a phase
          // from the vertex's own place in the build, weighted by how loose it
          // is, so a willow's curtains move one after another and its hem
          // after its crown, a crown's lobes not quite together — and a trunk,
          // whose flex is ~0, keeps the instance's one phase
          float loose = smoothstep(0.02, 0.10, flex);
          float ph2 = (position.x * 2.3 + position.z * 1.9 + position.y * 1.1) * loose;
          float g = sin(uTime * 1.35 + ph + ph2) * 0.5 + sin(uTime * 2.31 + ph * 1.7 + ph2 * 1.4) * 0.24;
          float sxz = max(length(instanceMatrix[0].xyz), 1e-3);
          float sy = length(instanceMatrix[1].xyz);
          transformed.xz += g * flex * (sy / sxz) * vec2(0.94, 0.34);
          // the lobe un-stretch rides the density morph: the mass's real
          // offset from its centre is pcen-scaled now, so its correction is too
          transformed.xz += lobe.xz * uPheno.y * (sy / sxz - 1.0);
          vVegW = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;
          vBurn = aBurn;
          vLeaf = leaf;
          vCure = cure;
        }`)
      // the crown's own place in the sun, for the transmission below: the
      // shadow map read a little ABOVE the plant's top (build space y 1.25 —
      // every kind's build tops out near 1), where nothing of its own can
      // occlude it, so it says whether THIS plant stands in open sun or under
      // a hill, a taller neighbour or a cloud — never that its own front
      // leaves shade its back ones, which is what a fragment's own coordinate
      // says of any solid crown and would switch the light off exactly where
      // it should come through
      .replace('#include <shadowmap_vertex>', `#include <shadowmap_vertex>
        #if defined( USE_SHADOWMAP ) && NUM_DIR_LIGHT_SHADOWS > 0
        {
          vec4 crownW = modelMatrix * instanceMatrix * vec4(0.0, 1.25, 0.0, 1.0);
          vCrownShadow = directionalShadowMatrix[ 0 ] * crownW;
        }
        #endif`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', `#include <common>
      varying vec3 vVegW;
      varying float vLeaf;
      varying float vCure;
      varying vec4 vCrownShadow;
      varying vec2 vBurn;
      uniform float uDry;
      float lHash(vec2 i) {
        vec2 w = mod(i, 256.0);
        vec3 q = fract(vec3(w.x, w.y, w.x) * 0.1031);
        q += dot(q, q.yzx + 33.33);
        return fract((q.x + q.y) * q.z);
      }
      vec3 lNoise(vec2 p) {
        vec2 i = floor(p), f = p - i;
        vec2 u = f * f * (3.0 - 2.0 * f);
        vec2 du = 6.0 * f * (1.0 - f);
        float a = lHash(i), b = lHash(i + vec2(1.0, 0.0));
        float c = lHash(i + vec2(0.0, 1.0)), d = lHash(i + vec2(1.0, 1.0));
        float k1 = b - a, k2 = c - a, k3 = a - b - c + d;
        return vec3(a + k1 * u.x + k2 * u.y + k3 * u.x * u.y,
                    du.x * (k1 + k3 * u.y),
                    du.y * (k2 + k3 * u.x));
      }`)
      .replace('#include <color_fragment>', `#include <color_fragment>
      {
        // The year, on the foliage. A MULTIPLY and never a mix toward an
        // absolute straw: this same instance colour already carries burn char
        // (x0.18), wither brown and the dead-plant straw, and an absolute mix
        // would wash all three back out — a charred stump would go golden in
        // August. Cutting blue hard and lifting red a little turns green to
        // hay while leaving anything already dark, dark.
        diffuseColor.rgb *= mix(vec3(1.0), vec3(1.16, 1.02, 0.55),
                                clamp(vCure, 0.0, 1.0) * uDry);
      }
      {
        /*
         * THE BURN, on colour. The instance colour already carries the char
         * (world/overlay.ts darkens a survivor by how hard its own ground
         * burned), so this is the part that colour alone could not say:
         *
         *  · **foliage scorches before it goes.** Between about a third and
         *    full char a leaf is brown and then black, and it is still on the
         *    tree — the vertex morph is only half-way through eating it. A
         *    crown that went from green straight to absent would read as a
         *    tree that had been edited rather than burned.
         *  · **wood silvers with age.** vBurn.y is the scar's own age, and
         *    a standing burnt trunk does not stay soot-black: it weathers
         *    grey over seasons, which is the difference between "there was a
         *    fire" and "there was a fire once".
         */
        if (vBurn.x > 0.002) {
          vec3 scorch = mix(vec3(0.22, 0.13, 0.07), vec3(0.05, 0.043, 0.04),
                            smoothstep(0.3, 0.9, vBurn.x));
          vec3 weathered = mix(vec3(0.06, 0.055, 0.05), vec3(0.40, 0.385, 0.36),
                               smoothstep(0.15, 0.85, vBurn.y));
          vec3 target = mix(weathered, scorch, vLeaf);
          diffuseColor.rgb = mix(diffuseColor.rgb, target,
                                 clamp(vBurn.x, 0.0, 1.0) * 0.85);
        }
      }
      {
        // Leaf texture, foliage only (vLeaf) — the first cut painted one
        // coarse octave over everything, and half-metre polka dots on
        // spotted trunks read as disease, not leaves. Now: a fine sharpened
        // speckle at leaf-cluster scale, biased toward dark GAPS (a canopy
        // is holes as much as leaves), over a gentle mass mottle. Wood keeps
        // a whisper of tonal breakup and no leaf pattern at all.
        float ld = length(vViewPosition);
        float fineL = 1.0 - smoothstep(40.0, 210.0, ld);
        float midL = 1.0 - smoothstep(150.0, 700.0, ld);
        if (midL > 0.002) {
          float sp = lNoise(vVegW.xz * 5.0 + vVegW.y * 3.4).x * 0.62
                   + lNoise(vec2(vVegW.y * 6.3, vVegW.x * 4.4 - vVegW.z * 3.7)).x * 0.38;
          float leafGrain = (sp - 0.5) * 0.09
                          - (1.0 - smoothstep(0.28, 0.55, sp)) * 0.13;
          float cl = lNoise(vVegW.xz * 1.3 + vVegW.y * 0.83).x;
          float grain = vLeaf * (leafGrain * fineL + (cl - 0.5) * 0.09 * midL)
                      + (1.0 - vLeaf) * (cl - 0.5) * 0.05 * midL;
          diffuseColor.rgb *= 1.0 + grain;
        }
      }`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
      {
        // the canopy's facets — foliage only, calmer than the first cut, and
        // gone by 240 m; past that the silhouette does the work. Bark keeps
        // its hard edges, which is what bark has.
        float nf = (1.0 - smoothstep(50.0, 240.0, length(vViewPosition))) * vLeaf;
        if (nf > 0.002) {
          vec3 g3 = lNoise(vVegW.xz * 3.6 + vVegW.y * 2.3).yzx;
          vec3 g2 = lNoise(vec2(vVegW.y * 3.8, vVegW.x * 2.9 - vVegW.z * 2.2)).yzx;
          vec3 wpert = vec3(g3.x + g2.y * 0.6, g2.x, g3.y - g2.y * 0.4);
          normal = normalize(normal + (viewMatrix * vec4(wpert * 0.3 * nf, 0.0)).xyz);
        }
      }`)
      // FOLIAGE TRANSMISSION (2026-08-18). A leaf is not a wall: the sun on
      // the far side of a crown comes through it, and the sun near the edge
      // of a crown reaches a little past the terminator. Without either, a
      // tree between the eye and a low sun was a black cut-out over a lit
      // hillside — the one thing in the close-range frame that read as flat.
      // Two terms, foliage only (vLeaf), on the FIRST directional light,
      // which three sorts to be the shadow-casting one — the sun:
      //  · through — light from behind the surface, seen from in front,
      //    at 0.30 of what a facing surface would take;
      //  · wrap — a soft continuation of the lambert term past grazing,
      //    strongest at the terminator, nothing where the surface already
      //    faces the light.
      // Both are gated by the crown's own place in the sun (vCrownShadow, the
      // shadow map read above the plant's top), not by the fragment's shadow
      // coordinate — a solid crown's front leaves would otherwise put its
      // back leaves in shade, and the light would go off exactly where it is
      // supposed to come through.
      .replace('#include <lights_fragment_end>', `#include <lights_fragment_end>
      #if NUM_DIR_LIGHTS > 0
      {
        float crownLit = 1.0;
        #if defined( USE_SHADOWMAP ) && NUM_DIR_LIGHT_SHADOWS > 0
        crownLit = getShadow( directionalShadowMap[ 0 ], directionalLightShadows[ 0 ].shadowMapSize,
                              directionalLightShadows[ 0 ].shadowBias, directionalLightShadows[ 0 ].shadowRadius,
                              vCrownShadow );
        #endif
        vec3 L = directionalLights[ 0 ].direction;
        float ndl = dot( geometryNormal, L );
        // through-light belongs to the RIM of a crown: at the silhouette the
        // sun crosses a leaf or two, in the middle it crosses the whole tree
        // — so the term falls off with how squarely the surface faces the eye,
        // and a backlit crown wears a bright edge over a dark heart instead of
        // glowing flat like a lantern (the first cut, 0.30 everywhere, did)
        float rim = pow( 1.0 - clamp( dot( geometryNormal, geometryViewDir ), 0.0, 1.0 ), 1.6 );
        float through = clamp( -ndl, 0.0, 1.0 ) * ( 0.04 + 0.34 * rim );
        float wrap = ( clamp( ( ndl + 0.5 ) / 1.5, 0.0, 1.0 ) - clamp( ndl, 0.0, 1.0 ) ) * 0.15;
        reflectedLight.directDiffuse += directionalLights[ 0 ].color * crownLit * vLeaf
          * ( through + wrap ) * BRDF_Lambert( material.diffuseColor );
      }
      #endif`);
  };

  return {
    geometry,
    near,
    material,
    depthMaterial,
    wind,
    phenoStructural(kind, p) {
      const e = PLANT_EXPRESSION[kind];
      const L = e.lift * clamp(p.crownLift, -2, 2);
      const dK = expressed(p.foliageDensity, e.density);
      const droop = e.droop * clamp(p.droop, -2, 2);
      const tK = expressed(p.trunkMass, e.trunk);
      // the common case, and the zero-cost one: structurally ordinary
      if (L === 0 && dK === 1 && droop === 0 && tK === 1) return null;
      const top = tops[kind];
      return { pheno: [L, dK, droop, tK], aux: [1 / top, top / (top + L), 0, 0] };
    },
    phenoApply(mesh, values) {
      const [pL, pD, pS, pT] = values.pheno;
      const [aI, aR] = values.aux;
      mesh.onBeforeRender = () => {
        phenoU.value.set(pL, pD, pS, pT);
        phenoAuxU.value.set(aI, aR, 0, 0);
      };
      mesh.onAfterRender = () => {
        phenoU.value.set(PHENO_NEUTRAL[0], PHENO_NEUTRAL[1], PHENO_NEUTRAL[2], PHENO_NEUTRAL[3]);
        phenoAuxU.value.set(PHENO_AUX_NEUTRAL[0], PHENO_AUX_NEUTRAL[1], PHENO_AUX_NEUTRAL[2], PHENO_AUX_NEUTRAL[3]);
      };
    },
    dispose() {
      for (const g of Object.values(geometry)) g.dispose();
      for (const arr of Object.values(near)) for (const g of arr) g.dispose();
      material.dispose();
      depthMaterial.dispose();
    },
  };
}

/**
 * THE CPU MIRROR of the shader's structural phenotype morph — the wildlife
 * `formVertex` pattern, applied to plants: the morph is spent in a vertex
 * shader no headless test can see, so this function IS the contract, kept in
 * the same file as the GLSL so neither can be edited alone. `test/phenotype.ts`
 * pushes real build vertices through it and asserts the semantic properties
 * (lift lifts, density scales masses, droop sinks the outer foliage, the top
 * never moves, the ground is never pierced). It is not called at runtime.
 *
 * Neutral values return the input EXACTLY — the same bit-exactness argument
 * the shader makes, testable here.
 */
export function plantMorphVertex(
  p: readonly [number, number, number],
  leaf: number,
  pcen: readonly [number, number, number],
  values: PlantPhenoValues,
): [number, number, number] {
  const [L, dK, droop, tK] = values.pheno;
  const [invTop, renorm] = values.aux;
  const sstep = (e0: number, e1: number, x: number) => {
    const t = clamp((x - e0) / (e1 - e0), 0, 1);
    return t * t * (3 - 2 * t);
  };
  // density: the mass scales about its own centre
  let x = p[0] + pcen[0] * (dK - 1);
  let y = p[1] + pcen[1] * (dK - 1);
  let z = p[2] + pcen[2] * (dK - 1);
  // trunk mass: wood thickens, fading with height
  const k = lerp(lerp(tK, 1, sstep(0.15, 0.7, y * invTop)), 1, leaf);
  x *= k; z *= k;
  // crown lift, renormalised so the top stays put
  y = (y + L * sstep(0.06, 0.55, y * invTop)) * renorm;
  // droop, floored at the part's own ground
  const sink = droop * (0.35 + 0.65 * leaf) * (x * x + z * z) * invTop;
  y -= Math.min(sink, Math.max(y - 0.01, 0));
  return [x, y, z];
}
